import React from 'react'
import cx from 'classnames'

import {
  ComparisonTableCell,
  ComparisonTableModuleProps,
  ComparisonTableRow,
} from './interfaces/ComparisonTableModule'

export type { ComparisonTableModuleProps } from './interfaces/ComparisonTableModule'

const CheckIcon = () => (
  <svg
    className="comparison-table__check-svg"
    width="12"
    height="10"
    viewBox="0 0 12 10"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M1 5.2 4.2 8.2 11 1"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
)

const fallbackCell: ComparisonTableCell = {
  _key: 'fallback',
  icon: 'dash',
}

const getCell = (row: ComparisonTableRow, columnIndex: number) =>
  row.cells?.[columnIndex] || fallbackCell

const CellContent = ({ cell }: { cell: ComparisonTableCell }) => {
  const icon = cell.icon || 'none'
  const showCheck = icon === 'check' || icon === 'mutedCheck'
  const showDash = icon === 'dash'

  return (
    <div
      className={cx(
        'comparison-table__cell-content',
        `comparison-table__cell-content--${cell.tone || 'default'}`,
        {
          'comparison-table__cell-content--icon-only': !cell.text,
        }
      )}
    >
      {showCheck && (
        <span
          className={cx('comparison-table__cell-icon', {
            'comparison-table__cell-icon--muted': icon === 'mutedCheck',
          })}
          aria-hidden="true"
        >
          <CheckIcon />
        </span>
      )}
      {showDash && (
        <span className="comparison-table__dash" aria-label="Not included">
          -
        </span>
      )}
      {cell.text && <span className="comparison-table__cell-text">{cell.text}</span>}
    </div>
  )
}

export const ComparisonTableModule = ({
  data,
}: {
  data: ComparisonTableModuleProps['data']
}) => {
  const {
    title,
    eyebrow,
    intro,
    featureColumnHeading,
    backgroundColor,
    spacing,
    deepLinkId,
  } = data
  const columns = data.columns?.slice(0, 3) || []
  const sections = data.sections || []
  const hasIntro = eyebrow || title || intro

  if (!columns.length || !sections.length) return null

  return (
    <section
      id={deepLinkId}
      className={cx('comparison-table-module', backgroundColor || 'bcoat', spacing || 'pym', {
        cw: backgroundColor === 'bcb',
      })}
    >
      <div className="container--l mxa outer p1">
        {hasIntro && (
          <header className="comparison-table__intro">
            {eyebrow && <p className="comparison-table__eyebrow">{eyebrow}</p>}
            {title && (
              <h2 className="comparison-table__title module-title-serif">
                {title}
              </h2>
            )}
            {intro && <p className="comparison-table__description">{intro}</p>}
          </header>
        )}

        <div className="comparison-table__surface">
          <div className="comparison-table__desktop" aria-hidden={false}>
            <table className="comparison-table__table">
              <thead>
                <tr>
                  <th scope="col" className="comparison-table__feature-heading">
                    {featureColumnHeading || 'What you get'}
                  </th>
                  {columns.map(column => (
                    <th
                      key={column._key}
                      scope="col"
                      className={cx('comparison-table__plan-heading', {
                        'comparison-table__plan-heading--highlighted':
                          column.highlighted,
                      })}
                    >
                      <span className="comparison-table__plan-title">
                        {column.heading}
                      </span>
                      {column.subheading && (
                        <span className="comparison-table__plan-subheading">
                          {column.subheading}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sections.map(section => (
                  <React.Fragment key={section._key}>
                    {section.heading && (
                      <tr className="comparison-table__section-row">
                        <th colSpan={columns.length + 1} scope="rowgroup">
                          {section.heading}
                        </th>
                      </tr>
                    )}
                    {section.rows?.map(row => (
                      <tr key={row._key} className="comparison-table__data-row">
                        <th scope="row" className="comparison-table__row-label">
                          {row.label}
                        </th>
                        {columns.map((column, columnIndex) => (
                          <td
                            key={column._key}
                            className={cx('comparison-table__value', {
                              'comparison-table__value--highlighted':
                                column.highlighted,
                            })}
                          >
                            <CellContent cell={getCell(row, columnIndex)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="comparison-table__mobile">
            {columns.map((column, columnIndex) => (
              <article
                key={column._key}
                className={cx('comparison-table__card', {
                  'comparison-table__card--highlighted': column.highlighted,
                })}
              >
                <header className="comparison-table__card-header">
                  <h3>{column.heading}</h3>
                  {column.subheading && <p>{column.subheading}</p>}
                </header>

                {sections.map(section => (
                  <div key={section._key} className="comparison-table__card-section">
                    {section.heading && (
                      <h4 className="comparison-table__card-section-heading">
                        {section.heading}
                      </h4>
                    )}
                    {section.rows?.map(row => (
                      <div key={row._key} className="comparison-table__card-row">
                        <p className="comparison-table__card-label">{row.label}</p>
                        <CellContent cell={getCell(row, columnIndex)} />
                      </div>
                    ))}
                  </div>
                ))}
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
